import { useState, useEffect, useCallback } from 'preact/hooks';
import { getUser, getUserScans } from '../../api/users';
import { Button } from '../../components/button';
import { Card } from '../../components/card';
import { Badge } from '../../components/badge';
import { Table, type Column } from '../../components/table';
import { Pagination } from '../../components/pagination';
import { Spinner } from '../../components/spinner';
import { Message } from '../../components/message';
import { EmptyState } from '../../components/empty-state';
import { EntityLink } from '../../components/entity-link';
import type { UserDetailResponse, RecentScan, UserFramework } from '../../api/types';

interface UserDetailProps {
  userId: string;
  onBack: () => void;
}

const SCANS_LIMIT = 10;

const frameworkColumns: Column<UserFramework>[] = [
  { header: 'Framework ID', key: 'framework_id' },
  {
    header: 'Enabled',
    key: 'is_enabled',
    render: (r) => {
      const enabled = r.is_enabled as unknown;
      return <Badge label={enabled ? 'Yes' : 'No'} variant={enabled ? 'ok' : 'warn'} />;
    },
  },
  { header: 'Priority', key: 'priority', render: (r) => String(r.priority ?? '\u2014') },
];

const scanColumns: Column<RecentScan>[] = [
  {
    header: 'Barcode',
    key: 'barcode',
    render: (r) => (
      <EntityLink type="product" id={r.barcode} mono />
    ),
  },
  { header: 'Product Name', key: 'product_name', render: (r) => r.product_name ?? '\u2014' },
  { header: 'Scanned At', key: 'scanned_at', render: (r) => new Date(r.scanned_at).toLocaleString() },
];

export function UserDetail({ userId, onBack }: UserDetailProps) {
  const [detail, setDetail] = useState<UserDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [scans, setScans] = useState<RecentScan[]>([]);
  const [scansTotal, setScansTotal] = useState(0);
  const [scansPage, setScansPage] = useState(1);
  const [scansLoading, setScansLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    getUser(userId)
      .then(setDetail)
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load user'))
      .finally(() => setLoading(false));
  }, [userId]);

  const loadScans = useCallback(async (page: number) => {
    setScansLoading(true);
    try {
      const res = await getUserScans(userId, { page, limit: SCANS_LIMIT });
      setScans(res.scans);
      setScansTotal(res.total);
    } catch {
      // scans error is non-critical
    } finally {
      setScansLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadScans(scansPage);
  }, [scansPage, loadScans]);

  if (loading) return <Spinner text="Loading user" centered />;
  if (error) return <Message text={error} type="error" />;
  if (!detail) return null;

  const user = detail.user;
  const prefs = detail.preferences;

  return (
    <div>
      {/* Header */}
      <div class="toolbar">
        <Button variant="ghost" size="sm" onClick={onBack}>
          &larr; Back to users
        </Button>
      </div>

      {/* User Info */}
      <Card>
        <h2>User Info</h2>
        <div class="detail-grid">
          <div class="detail-label">ID</div>
          <div class="detail-value" style={{ wordBreak: 'break-all' }}>{user.id as string}</div>
          <div class="detail-label">Created</div>
          <div class="detail-value">{new Date(user.created_at as string).toLocaleString()}</div>
          <div class="detail-label">Total Scans</div>
          <div class="detail-value">{detail.scanCount}</div>
        </div>
      </Card>

      {/* Preferences */}
      {prefs && (
        <Card>
          <h2>Preferences</h2>
          <div class="detail-grid">
            {Object.entries(prefs)
              .filter(([k]) => k !== 'user_id')
              .map(([key, value]) => (
                <>
                  <div class="detail-label">{key}</div>
                  <div class="detail-value">{String(value ?? '\u2014')}</div>
                </>
              ))}
          </div>
        </Card>
      )}

      {/* Frameworks */}
      {detail.frameworks.length > 0 && (
        <Card>
          <h2>Frameworks</h2>
          <Table
            columns={frameworkColumns as unknown as Column<Record<string, unknown>>[]}
            rows={detail.frameworks as unknown as Record<string, unknown>[]}
          />
        </Card>
      )}

      {/* Scans */}
      <Card>
        <h2>Recent Scans</h2>
        {scansLoading ? (
          <Spinner text="Loading scans" centered />
        ) : scans.length === 0 ? (
          <EmptyState icon="search" message="No scans recorded" />
        ) : (
          <>
            <Table
              columns={scanColumns as unknown as Column<Record<string, unknown>>[]}
              rows={scans as unknown as Record<string, unknown>[]}
            />
            <Pagination
              page={scansPage}
              total={scansTotal}
              limit={SCANS_LIMIT}
              onPageChange={setScansPage}
            />
          </>
        )}
      </Card>
    </div>
  );
}
